import { isRelativeTimeCondition } from '@/components/grouped-filter-bar/groupedFilterFields'
import { convertTimeRangeToMs } from '@/config/utils'
import type { FilterGroup, FilterState } from '@/types/deviceFilters'
import {
	type DeviceStatusDefinition,
	type DeviceStatusExpression,
	defaultStatusDefinition,
	matchableStatusDefinitions,
} from '@/types/deviceStatus'
import { asList } from '@/utils/asList'

/**
 * Resolve a derived column against one device's own state, client-side.
 *
 * The list view asks `/search` which devices match each state, but that needs a real column and a
 * derived one is not materialised. With a single device in hand the same conditions can be evaluated
 * directly, which is both cheaper and works today.
 */
export type DeviceStateRecord = Record<string, unknown>

const toNumber = (value: unknown): number | null => {
	if (typeof value === 'number') {
		return Number.isFinite(value) ? value : null
	}
	if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) {
		return Number(value)
	}
	return null
}

/** Values arrive as strings from the filter editor; device state keeps its native type. */
const looseEquals = (deviceValue: unknown, candidate: string): boolean => {
	if (deviceValue === null || deviceValue === undefined) {
		return false
	}
	if (typeof deviceValue === 'boolean') {
		return String(deviceValue) === candidate.trim().toLowerCase()
	}
	const left = toNumber(deviceValue)
	const right = toNumber(candidate)
	if (left !== null && right !== null) {
		return left === right
	}
	return String(deviceValue).toLowerCase() === candidate.trim().toLowerCase()
}

const matchesRelativeTime = (deviceValue: unknown, values: string[], negated: boolean, now: number): boolean => {
	const windowMs = Number(convertTimeRangeToMs(values[0] ?? ''))
	const observed = Date.parse(String(deviceValue))
	if (!Number.isFinite(windowMs) || windowMs <= 0 || Number.isNaN(observed)) {
		return false
	}
	const withinWindow = now - observed <= windowMs
	return negated ? !withinWindow : withinWindow
}

export const matchesCondition = (condition: FilterState, state: DeviceStateRecord, now = Date.now()): boolean => {
	const deviceValue = state[condition.field]
	const operator = condition.operator

	if (operator === 'is_null') {
		return deviceValue === null || deviceValue === undefined
	}
	if (operator === 'is_not_null') {
		return deviceValue !== null && deviceValue !== undefined
	}

	const values = (Array.isArray(condition.value) ? condition.value : [condition.value])
		.filter(value => value !== null && value !== undefined)
		.map(String)
	if (values.length === 0) {
		return false
	}

	if (isRelativeTimeCondition(condition)) {
		return matchesRelativeTime(deviceValue, values, operator === 'is_not' || operator === 'not_in', now)
	}

	switch (operator) {
		case 'is':
		case 'in':
		case '=':
			return values.some(value => looseEquals(deviceValue, value))
		case 'is_not':
		case 'not_in':
		case '!=':
			return !values.some(value => looseEquals(deviceValue, value))
		case '>':
		case '<':
		case '>=':
		case '<=': {
			const left = toNumber(deviceValue)
			const right = toNumber(values[0])
			if (left === null || right === null) {
				return false
			}
			return operator === '>'
				? left > right
				: operator === '<'
					? left < right
					: operator === '>='
						? left >= right
						: left <= right
		}
		default:
			return false
	}
}

const matchesGroup = (group: FilterGroup, state: DeviceStateRecord, now: number): boolean => {
	const conditions = group.conditions ?? []
	return group.operator === 'or'
		? conditions.some(condition => matchesCondition(condition, state, now))
		: conditions.every(condition => matchesCondition(condition, state, now))
}

/** Groups fold left-associatively on each group's joiner, mirroring `buildGroupedDeviceSearchFilters`. */
export const matchesConditionGroups = (groups: FilterGroup[], state: DeviceStateRecord, now = Date.now()): boolean => {
	const usable = groups.filter(group => (group.conditions ?? []).length > 0)
	if (usable.length === 0) {
		return false
	}
	let result = matchesGroup(usable[0], state, now)
	for (let index = 1; index < usable.length; index++) {
		const joiner = usable[index - 1].joiner === 'and' ? 'and' : 'or'
		result =
			joiner === 'and'
				? result && matchesGroup(usable[index], state, now)
				: result || matchesGroup(usable[index], state, now)
	}
	return result
}

/**
 * The state a device resolves to — array position is match order, so the first match wins. A device
 * matching nothing (including one reporting no state at all) falls to the default state, if the
 * column defines one.
 */
export const matchDerivedState = (
	expression: DeviceStatusExpression,
	state: DeviceStateRecord | undefined,
	now = Date.now(),
): DeviceStatusDefinition | null => {
	const fallback = defaultStatusDefinition(expression)
	if (!state) {
		return fallback
	}
	for (const definition of matchableStatusDefinitions(expression)) {
		if (Array.isArray(definition.condition) && matchesConditionGroups(definition.condition, state, now)) {
			return definition
		}
	}
	return fallback
}

/**
 * The device_state columns a derived state reads — every field its conditions match on, in first-seen
 * order, as `display_name`s. Tolerant of unvalidated JSONB, as the rest of this module is.
 */
export const expressionSourceFields = (expression: DeviceStatusExpression): string[] => {
	const fields: string[] = []
	for (const definition of expression) {
		for (const group of asList(definition.condition)) {
			for (const condition of group.conditions ?? []) {
				if (condition.field && !fields.includes(condition.field)) {
					fields.push(condition.field)
				}
			}
		}
	}
	return fields
}
